import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, STAGE_META } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Layout from "@/components/Layout";
import { Plus, Trash2, ArrowRight, CheckCircle2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import ConflictPlanDialog from "@/components/ConflictPlanDialog";

export default function Dashboard() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [planSessionId, setPlanSessionId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sessions");
      setSessions(r.data);
    } catch {
      toast.error("Could not load your sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createSession = async () => {
    try {
      const r = await api.post("/sessions", { title: newTitle.trim() || "Untitled conflict" });
      setShowCreate(false);
      setNewTitle("");
      navigate(`/session/${r.data.id}`);
    } catch {
      toast.error("Could not create session");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sessions/${deleteTarget.id}`);
      setSessions((s) => s.filter((x) => x.id !== deleteTarget.id));
      toast.success("Session deleted");
    } catch {
      toast.error("Could not delete");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16 fade-in">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Your sessions</p>
            <h1 className="text-3xl sm:text-4xl tracking-tight" style={{ fontFamily: 'Manrope' }}>
              A quiet place to think.
            </h1>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="rounded-full h-12 px-6"
            data-testid="new-session-button"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            New session
          </Button>
        </div>

        {loading ? (
          <div className="text-muted-foreground" data-testid="dashboard-loading">Loading…</div>
        ) : sessions.length === 0 ? (
          <Card className="p-12 rounded-3xl border-border/60 shadow-none text-center" data-testid="empty-state">
            <p className="text-foreground/70 mb-6">
              You haven't started a session yet. When you're ready, begin your first one.
            </p>
            <Button
              onClick={() => setShowCreate(true)}
              className="rounded-full h-11 px-6"
              data-testid="empty-state-new-button"
            >
              Start your first session
            </Button>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="session-list">
            {sessions.map((s) => {
              const done = !!s.final_summary;
              const stage = s.current_stage ?? 0;
              return (
                <Card
                  key={s.id}
                  className="p-6 rounded-2xl border-border/60 shadow-none hover:shadow-sm transition-shadow group cursor-pointer"
                  onClick={() => navigate(`/session/${s.id}`)}
                  data-testid={`session-card-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {done ? (
                          <span className="inline-flex items-center gap-1 text-xs text-primary">
                            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Completed
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Step {Math.min(stage, 4) + 1} of 5 · {STAGE_META[Math.min(stage, 4)]?.short}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-medium truncate mb-1" style={{ fontFamily: 'Manrope' }}>
                        {s.title}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Updated {new Date(s.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {done && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-full text-primary hover:text-primary hover:bg-primary/10"
                          onClick={(e) => { e.stopPropagation(); setPlanSessionId(s.id); }}
                          data-testid={`open-plan-${s.id}`}
                        >
                          <ClipboardList className="w-4 h-4 mr-1" strokeWidth={1.5} /> Plan
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                        data-testid={`delete-session-${s.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-3xl" data-testid="new-session-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Name this session</DialogTitle>
            <DialogDescription>Give this conflict a short name so you can find it later.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              placeholder="e.g. Disagreement with my sister about mom's care"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="rounded-xl h-12"
              data-testid="new-session-title-input"
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-2">You can rename this anytime.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
            <Button onClick={createSession} className="rounded-full" data-testid="confirm-new-session">
              Begin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this session?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes all of its messages and summaries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-session"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConflictPlanDialog
        open={!!planSessionId}
        onOpenChange={(o) => !o && setPlanSessionId(null)}
        sessionId={planSessionId}
      />
    </Layout>
  );
}
